// DS row 106 Compare: the duplicate-check grid (C-7 … C-10). Attribute rows (*Primary email · Secondary email · Name ·
// Phone · LinkedIn URL · Candidate history*) × record columns, each headed by its Candidate ID (IdPill), the name and
// the profile status. A cell whose value matches across records is on --t-gold in --gold-text (a matched cell, DS
// §1.4); an empty value prints "—"; a record outside the viewer's scope is masked per DC-28 (--text-3 on --t-neutral,
// the words *subject to permission*, no value). The actions are his: *Use existing* and *Create distinct*.
// `preview` (the merge form): one native radio group per attribute row (the browser's single tab stop, arrows inside),
// and a *Preview* column showing the chosen values under the kept profile's Candidate ID; *Merge* stays dashed with
// his rule sentence until at least one choice is made. There is no "Surviving record" (DS §3.4). Each radio is named
// by its attribute row's head and its record column's head (aria-labelledby the two ids, never a joined string);
// `hs` on an attribute is one hotspot per record.

import { useId } from "react";
import { Button, IdPill, Pill, Table, Td, Th, Tr, type Blocked } from "../core";
import { ACTION_LABELS } from "../labels";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { G6_LABELS, G6_RULE_LABELS } from "./labels";

export interface CompareRecord {
  /** The Candidate ID (CAN-2026-000123). */
  id: string;
  name: string;
  /** The profile status word (*Active*, *Merged*). */
  status?: string | undefined;
  /** Outside the viewer's scope: every cell is masked (DC-28). */
  masked?: boolean | undefined;
}

export interface CompareAttribute {
  key: string;
  /** The attribute's words (a label). */
  label: string;
  /** One value per record, in record order; null or "" prints "—". */
  values: readonly (string | null | undefined)[];
  /** The records whose value matches (drawn on --t-gold). */
  matched?: readonly number[] | undefined;
  /** The merge form's radio hotspots, one per record, in record order. */
  hs?: readonly Hs[] | undefined;
}

export interface CompareAction {
  onClick?: (() => void) | undefined;
  blocked?: Blocked | undefined;
  busy?: boolean | undefined;
  hs?: Hs | undefined;
}

export interface ComparePreview {
  /** The chosen record per attribute key. */
  value: Readonly<Record<string, number | undefined>>;
  onChange?: ((attribute: string, record: number) => void) | undefined;
  /** The kept profile (a record index), named by its Candidate ID over the Preview column. */
  keep: number;
  /** The Merge action; blocked until at least one choice is made. */
  merge?: CompareAction | undefined;
}

export interface CompareProps {
  /** The grid's name (default *Potential matches*). */
  label?: string | undefined;
  records: readonly CompareRecord[];
  attributes: readonly CompareAttribute[];
  /** The two decisions of the duplicate check (absent: not drawn). */
  useExisting?: CompareAction | undefined;
  createDistinct?: CompareAction | undefined;
  /** The merge form. */
  preview?: ComparePreview | undefined;
  /** false: the seat may not see the grid. */
  can?: boolean | undefined;
  className?: string | undefined;
}

const EMPTY = "—";
const shown = (value: string | null | undefined) => (value === null || value === undefined || value === "" ? EMPTY : value);

function Pick({ name, labelledBy, value, checked, onChange, hs }: {
  name: string;
  /** The attribute row head's id and the record column head's id. */
  labelledBy: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  hs?: Hs | undefined;
}) {
  const hot = useHs(hs);
  return (
    <label className="mc-cmp-pick">
      <input type="radio" name={name} checked={checked} onChange={onChange} aria-labelledby={labelledBy} {...hot} />
      <span>{value}</span>
    </label>
  );
}

export function Compare({ label = G6_LABELS.potentialMatches, records, attributes, useExisting, createDistinct, preview, can, className }: CompareProps) {
  const group = useId();
  if (can === false) return null;
  const kept = preview ? records[preview.keep] : undefined;
  const chosen = preview ? Object.values(preview.value).filter((index) => index !== undefined).length : 0;
  const head = (
    <Tr>
      <Th><VisuallyHidden>{label}</VisuallyHidden></Th>
      {records.map((record, index) => (
        <Th key={record.id}>
          {record.masked ? (
            <span className="mc-cmp-rec mc-cmp-masked">{G6_LABELS.subjectToPermission}</span>
          ) : (
            <span className="mc-cmp-rec" id={`${group}-c${index}`}>
              <IdPill value={record.id} />
              <b>{record.name}</b>
              {record.status ? <Pill>{record.status}</Pill> : null}
            </span>
          )}
        </Th>
      ))}
      {preview ? (
        <Th className="mc-cmp-pv-h">
          <span className="mc-cmp-rec">
            <span>{G6_LABELS.preview}</span>
            {kept ? <span className="mc-mono">{kept.id}</span> : null}
          </span>
        </Th>
      ) : null}
    </Tr>
  );
  const merge = preview?.merge;
  const mergeBlocked = merge?.blocked ?? (chosen === 0 ? { reason: G6_RULE_LABELS.neverAutoMerge } : undefined);
  const actions = preview
    ? merge
      ? <Button tone="primary" sm onClick={merge.onClick} blocked={mergeBlocked} busy={merge.busy} hs={merge.hs}>{ACTION_LABELS.merge}</Button>
      : null
    : (
      <>
        {useExisting ? (
          <Button tone="primary" sm onClick={useExisting.onClick} blocked={useExisting.blocked} busy={useExisting.busy} hs={useExisting.hs}>{G6_LABELS.useExisting}</Button>
        ) : null}
        {createDistinct ? (
          <Button tone="ghost" sm onClick={createDistinct.onClick} blocked={createDistinct.blocked} busy={createDistinct.busy} hs={createDistinct.hs}>{G6_LABELS.createDistinct}</Button>
        ) : null}
      </>
    );
  const hasActions = preview ? Boolean(merge) : Boolean(useExisting || createDistinct);
  return (
    <div className={cx("mc-cmp", preview && "mc-cmp-preview", className)}>
      <Table dense stickyFirst label={label} head={head}>
        {attributes.map((attribute) => {
          const pick = preview?.value[attribute.key];
          const pickedRecord = pick === undefined ? undefined : records[pick];
          return (
            <Tr key={attribute.key}>
              <Th row><span id={`${group}-r-${attribute.key}`}>{attribute.label}</span></Th>
              {records.map((record, index) => {
                if (record.masked) {
                  return <Td key={record.id} label={attribute.label} className="mc-cmp-masked">{G6_LABELS.subjectToPermission}</Td>;
                }
                const value = shown(attribute.values[index]);
                const matched = attribute.matched?.includes(index) ?? false;
                return (
                  <Td key={record.id} label={attribute.label} className={cx(matched && "mc-cmp-m", value === EMPTY && "mc-cmp-e") || undefined}>
                    {preview && value !== EMPTY ? (
                      <Pick
                        name={`${group}-${attribute.key}`}
                        labelledBy={`${group}-r-${attribute.key} ${group}-c${index}`}
                        value={value}
                        checked={pick === index}
                        onChange={() => preview.onChange?.(attribute.key, index)}
                        hs={attribute.hs?.[index]}
                      />
                    ) : value}
                  </Td>
                );
              })}
              {preview ? (
                <Td label={G6_LABELS.preview} className="mc-cmp-pv">
                  {pickedRecord ? shown(attribute.values[pick ?? 0]) : EMPTY}
                </Td>
              ) : null}
            </Tr>
          );
        })}
      </Table>
      {hasActions ? <div className="mc-cmp-a">{actions}</div> : null}
    </div>
  );
}
